import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {MessageBar, MessageBarType, PrimaryButton, Spinner} from "office-ui-fabric-react";
import * as microsoftTeams from "@microsoft/teams-js";
import {get, hostserver, post} from "../api";

const ArchiveStatus = {
    Started: 'Started',
    Success: 'Success',
    Fail: 'Fail'
};

export interface IArchiveTeamsPostsState extends ITeamsBaseComponentState {
    tenantId: string;
    teamsId: string;
    authenticatedToSp: boolean;
    loading: boolean;
    error: string;
    userInTeam: boolean;
    saveSuccessful: boolean;
    archivingPosts: boolean;
    archiveStatus: string;
}

export interface IArchiveTeamsPostsProps {}

export class ArchiveTeamsPosts extends TeamsBaseComponent<IArchiveTeamsPostsProps, IArchiveTeamsPostsState> {
    constructor(props: IArchiveTeamsPostsProps) {
        super(props);
        this.state = {
            theme: null,
            tenantId: this.getQueryVariable("tenantId"),
            teamsId: this.getQueryVariable("teamsId"),
            authenticatedToSp: document.cookie.indexOf("ConConArchivePosts=") != -1 ? true : false,
            loading: true,
            error: "",
            userInTeam: false,
            saveSuccessful: false,
            archivingPosts: true,
            archiveStatus: "",
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
    }

    public async componentDidMount(): Promise<void> {
        if (this.state.authenticatedToSp) {
            this.getTeamOwners();
            this.pollForArchiveStatus(true);
        }
    }

    getTeamOwners = async () => {
        await get("/api/teamsdmsintegration/IsUserTeamOwner?teamsId=" + this.state.teamsId)
            .then((r) => {
                if (r.status.toString().startsWith("2")) {
                    return r.json();
                } else {
                    this.setState({error: "Unable to get Team owners."});
                }
            })
            .then((response) => {
                const userInTeam = response as boolean;

                this.setState({userInTeam: userInTeam, loading: false});

                if (!userInTeam) {
                    this.setState({error: "User is not a Team owner. Unable to archive posts."});
                }
            });
    };

    archiveTeamsPosts = async () => {
        this.setState({archivingPosts: true, error: ""});

        post("/api/teamsdmsintegration/ArchiveTeamsPosts?teamsId=" + this.state.teamsId, null);

        this.pollForArchiveStatus(false);
    };

    pollForArchiveStatus = async (firstLoad: boolean) => {
        let interval = 0;
        let pollCount = 0;

        if (!firstLoad) {
            interval = 10000;
        }

        const pollForInfo = () => {
            pollCount++;
            if (pollCount > 4) {
                interval = 20000;
            }

            get("/api/teamsdmsintegration/PollArchiveTeamsPostsResults?teamsId=" + this.state.teamsId)
                .then((r) => {
                    if (r.status.toString().startsWith("2")) {
                        return r.json();
                    } else {
                        this.setState({error: "Failed to archive all posts"});
                    }
                })
                .then((response) => {
                    const status = response as string;
                    if (status == null) {
                        // When loading the page for the 1st time, cache will be empty.
                        this.setState({archivingPosts: false});
                    }
                    else if (status == ArchiveStatus.Success) {
                        this.setState({
                            saveSuccessful: true, 
                            archivingPosts: false, 
                            archiveStatus: "",
                            error: ""}
                        );
                    } else if (status == ArchiveStatus.Fail) {
                        this.setState({
                            saveSuccessful: false, 
                            archivingPosts: false, 
                            archiveStatus: "",
                            error: "Failed to archive all posts"}
                        );
                    } else if (status == ArchiveStatus.Started) {
                        // when an archiving process is in progress
                        this.setState({
                            saveSuccessful: false, 
                            archivingPosts: true, 
                            archiveStatus: "",
                            error: ""
                        });
                        setTimeout(pollForInfo, interval);
                    }                     
                    else {
                        // When PDF Generation is running
                        this.setState({
                            saveSuccessful: false, 
                            archivingPosts: true,
                            archiveStatus: status,
                            error: ""
                        });
                        setTimeout(pollForInfo, interval);
                    }
                })
                .catch(error => {
                    this.setState({
                        saveSuccessful: false, 
                        archivingPosts: true, //To make sure the button is not shown
                        archiveStatus: "",
                        error: "Failed to retrieve archive status. Please refresh the page and try again."
                    });
                    console.log(error);
                });
        };

        setTimeout(pollForInfo, interval);
    };

    private doSPLogin() {
        const loginUrl =
            hostserver() +
            `/DMSConnector/StartUserLoginFlow?tenantId=${this.state.tenantId},${this.state.teamsId},archivePosts`;

        window.location.replace(loginUrl);
    }

    public render() {
        if (!this.state.authenticatedToSp) {
            return (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    isMultiline={false}
                    actions={
                        <div>
                            <PrimaryButton onClick={this.doSPLogin.bind(this)} text={"Log In"} />
                        </div>
                    }
                >
                    You are not currently logged in. Log in to archive Teams posts.
                </MessageBar>
            );
        }

        if (this.state.loading) {
            return <Spinner />;
        }

        return (
            <div style={{paddingLeft: "20px", paddingTop: "20px"}}>
                <h1 style={{fontSize: "20px"}}>Archive Teams Posts</h1>
                <br></br>
                {this.state.userInTeam && (
                    <div>
                        {!this.state.saveSuccessful && !this.state.archivingPosts && (
                            <PrimaryButton
                                onClick={this.archiveTeamsPosts}
                                text={"Archive Posts"}
                                className={"authinput"}
                            />
                        )}
                        {this.state.archiveStatus && <div>{this.state.archiveStatus}</div>}
                        {this.state.archiveStatus && <br></br>}
                        {this.state.archivingPosts && <Spinner className={"authinput"} />}
                        {this.state.saveSuccessful && (
                            <MessageBar
                                messageBarType={MessageBarType.success}
                                isMultiline={true}
                                className={"successMessageBox"}
                            >
                                <div style={{fontSize: "14px"}}>{"All posts archived successfully"}</div>
                            </MessageBar>
                        )}
                    </div>
                )}
                <br></br>
                {this.state.error && <div style={{color: "red"}}>{this.state.error}</div>}
            </div>
        );
    }
}
